import SelectMissionModal from '../../Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectMissionModal/SelectMissionModal'
import SelectRobotModal from '../../Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectRobotModal/SelectRobotModal'
import { parseWallClockFromDatetime, setWallClockOnDate, toISOStringYYYYMMDD } from '../TaskSchedulerUtils'
import { hasRecurringWeekdays, withSingleOccurrenceReschedule } from 'utils/odysseyReschedule'
import { MainApi } from 'api/MainApi'
import { type GausiumExecutableTaskRecent, RobotApi } from 'api/RobotApi'
import {
  MainService,
  OdysseyRead,
  RobotRead,
  SchedulerSettings as SchedulerSettingsType,
  TaskTypeEnum,
} from 'api/schema'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import AdaptiveTabs from 'components/AdaptiveTabs/AdaptiveTabs'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import PrerequisitesSettingsForm from 'components/PrerequisitesSettings/PrerequisitesSettingsForm'
import { FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS } from 'config/featureFlags'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import Modal from 'react-modal'
import { GAUSIUM_REMOTE_COMMAND_START_DELAY_MS, MISSION_CALCULATING_STATUSES, TASK_STATUSES } from 'utils/constants'
import {
  DIAGNOSTIC_LEVEL,
  createDiagnosticStatusClassGetter,
  getDiagnosticStatusText,
  shouldShowDiagnostics as shouldShowDiagnosticsUtil,
} from 'utils/diagnosticsUtils'
import { IMissionInfo } from 'utils/interfaces'
import {
  OdysseyStartSettings,
  mergeOdysseySettings,
} from 'utils/odysseySettings'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import { dispatchBlockReason, robotTypesAcceptingWork } from 'utils/robotDispatch'
import { isSweeperRobotType } from 'utils/servicePointsCatalog'
import { VENDOR, type VendorCode } from 'utils/vendorDisplayName'

import {
  type DialogContext,
  type DialogTarget,
  type PickerScope,
  type Preselected,
  derivePolicy,
} from './DialogContext'
import SchedulerSettings from './SchedulerSettings/SchedulerSettings'

import diagnosticStyles from '../../Robot/RobotView/RobotLeftBlock/RobotDetails/RobotDetails.module.css'
import style from './TaskSettingsModal.module.css'

// Robot diagnostics polling interval (in milliseconds)
const ROBOT_DIAGNOSTICS_POLLING_INTERVAL = 5000 // 5 seconds

/** Fallback min remaining % when profile missing or metric has too few samples */
const DEFAULT_AUTONOMICS_RESOURCE_THRESHOLD_PCT = 50

type RobotReadWithTimezone = RobotRead & { timezone?: string | null }

const getRobotTimezone = (robot: RobotRead | undefined) => {
  const timezone = (robot as RobotReadWithTimezone | undefined)?.timezone
  return timezone || undefined
}

/**
 * TaskSettingsModal props are split into three orthogonal semantic blocks
 * (`target` / `lockOverride` / `pickerScope`) and a separate payload block
 * (`preselectedData`) that carries raw objects used to prefill the form.
 * Field-level editability is derived inside the component (see `dialogContext.ts`).
 */
interface IDialogProps {
  isOpen: boolean
  taskName: string
  onClose: (refresh: boolean) => void
  onSubmit: (refresh: boolean) => void

  /** What the dialog does: new / edit whole task / edit single occurrence. */
  target: DialogTarget
  /**
   * Optional lock override for rare cases where inferred lock context from payload
   * must be force-adjusted by caller.
   */
  lockOverride?: Preselected
  /** Allowed set for pickers (intersection with any internal filters). */
  pickerScope?: PickerScope

  /** Raw payload used to prefill the form (not the same as `preselected`). */
  preselectedData?: {
    /** Task to edit (target.kind !== 'new') or to re-launch (target.kind === 'new'). */
    task?: OdysseyRead
    /** Mission payload for launch. */
    mission?: IMissionInfo
    /** Scenario payload for launch (Autonomics or GS-scenario shape). */
    scenario?: any
    /** Gausium executable task payload. */
    gausiumExecutableTask?: GausiumExecutableTaskRecent
    /** Legacy GS map pk — launch via GS remote command (kept as a separate payload path). */
    gausiumMapPk?: number
    /** Optional robot id for payloads that don't include full robot object. */
    robotId?: number
  }

  /** Date of the single occurrence being edited (UI-only; semantics live in `target`). */
  dateToEdit?: Date | null
  initialTab?: number
  displayOffsetMinutes?: number
  initialTaskOffsetMinutes?: number
}

const TaskSettingsModal = ({
  isOpen,
  taskName,
  onClose,
  onSubmit,
  target,
  lockOverride,
  pickerScope,
  preselectedData,
  dateToEdit,
  initialTab,
  displayOffsetMinutes,
  initialTaskOffsetMinutes,
}: React.PropsWithChildren<IDialogProps>) => {
  useSuppressNotificationSandwich(isOpen, NOTIFICATION_SANDWICH_SUPPRESS.TASK_SETTINGS_MODAL)
  const taskToEdit: OdysseyRead | undefined = target.kind !== 'new' ? preselectedData?.task : undefined
  const eventScope: string | undefined =
    target.kind === 'task_occurrence' ? 'current' : target.kind === 'task_all' ? 'all' : undefined
  const robotIdsFilter = pickerScope?.robotIds
  const selectRobotIncludeVendor = pickerScope?.vendor
  const legacyLaunchItem = useMemo(() => {
    if (target.kind !== 'new') return undefined
    if (
      !preselectedData?.task &&
      !preselectedData?.mission &&
      !preselectedData?.scenario &&
      !preselectedData?.gausiumMapPk &&
      !preselectedData?.gausiumExecutableTask &&
      !preselectedData?.robotId &&
      !lockOverride?.robot
    ) {
      return undefined
    }
    return {
      task: preselectedData?.task,
      mission: preselectedData?.mission,
      scenario: preselectedData?.scenario,
      gausiumMapPk: preselectedData?.gausiumMapPk,
      gausiumExecutableTask: preselectedData?.gausiumExecutableTask,
      robotId: preselectedData?.robotId ?? lockOverride?.robot?.id,
    }
  }, [
    target.kind,
    preselectedData?.task,
    preselectedData?.mission,
    preselectedData?.scenario,
    preselectedData?.gausiumMapPk,
    preselectedData?.gausiumExecutableTask,
    preselectedData?.robotId,
    lockOverride?.robot?.id,
  ])
  const inferredLocks = useMemo<Preselected>(() => {
    const robotId = preselectedData?.robotId ?? preselectedData?.gausiumExecutableTask?.robot_id
    const cleaningItem = (() => {
      if (preselectedData?.gausiumExecutableTask?.id != null) {
        return { type: 'executable_task' as const, id: preselectedData.gausiumExecutableTask.id }
      }
      if ((preselectedData?.scenario as any)?.id != null) {
        return { type: 'scenario' as const, id: (preselectedData?.scenario as any).id }
      }
      if (preselectedData?.mission?.id != null) {
        return { type: 'mission' as const, id: preselectedData.mission.id }
      }
      return undefined
    })()
    return {
      ...(robotId != null ? { robot: { id: robotId } } : {}),
      ...(cleaningItem ? { cleaningItem } : {}),
    }
  }, [
    preselectedData?.robotId,
    preselectedData?.gausiumExecutableTask,
    preselectedData?.scenario,
    preselectedData?.mission,
  ])
  const effectiveLocks: Preselected = {
    ...inferredLocks,
    ...(lockOverride ?? {}),
    ...(lockOverride?.robot ? { robot: lockOverride.robot } : {}),
    ...(lockOverride?.cleaningItem ? { cleaningItem: lockOverride.cleaningItem } : {}),
  }
  const { t } = useTranslation()
  const [tempTaskName, setTempTaskName] = useState(taskName)
  const [hasNameError, setNameHasError] = useState(false)
  const [showMissionModal, setShowMissionModal] = useState(false)
  const [showRobotModal, setShowRobotModal] = useState(false)
  const [selectedMission, setSelectedMission] = useState<IMissionInfo | any | undefined>(undefined)
  const [selectedScenario, setSelectedScenario] = useState<any | undefined>(undefined)
  const [selectedRobot, setSelectedRobot] = useState<RobotRead | undefined>(undefined)
  const [tab, setTab] = useState(0)
  const [date, setDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [selectedWeekdays, setSelectedWeekdays] = useState<boolean[]>([false, false, false, false, false, false, false])
  const [robot_type, setRobotType] = useState<string | undefined>(undefined)
  const [gausiumMapName, setGausiumMapName] = useState('')
  const [gausiumCleaningMode, setGausiumCleaningMode] = useState('__middle_cleaning')
  const [gausiumLoading, setGausiumLoading] = useState(false)
  const [gausiumError, setGausiumError] = useState<string | null>(null)
  const [resourceWarningOpen, setResourceWarningOpen] = useState(false)
  const [problematicResources, setProblematicResources] = useState<string[]>([])
  const [prerequisites, setPrerequisites] = useState<OdysseyStartSettings>(() => mergeOdysseySettings())
  const [vendorFilter, setVendorFilter] = useState<VendorCode>(selectRobotIncludeVendor ?? VENDOR.AUTONOMICS)
  const robotTimezone = getRobotTimezone(selectedRobot)

  useEffect(() => {
    if (selectedRobot?.robot_type) {
      setRobotType(selectedRobot.robot_type)
    } else {
      const mission = selectedMission || selectedScenario
      if (mission?.robot_type) {
        setRobotType(mission.robot_type)
      } else {
        setRobotType(undefined)
      }
    }
  }, [selectedRobot, selectedMission, selectedScenario])

  // Check if robot has blocking diagnostics (ERROR or STALE)
  const hasBlockingDiagnostics = () => {
    const level = selectedRobot?.system_diagnostics?.level
    return level === DIAGNOSTIC_LEVEL.ERROR || level === DIAGNOSTIC_LEVEL.STALE
  }

  // Check if task is instant (no date/time scheduled)
  const isInstantTask = () => {
    return !date && areArraysEqual(selectedWeekdays, [false, false, false, false, false, false, false])
  }

  // Check if save should be blocked due to diagnostics
  const shouldBlockSave = () => {
    return hasBlockingDiagnostics() && isInstantTask()
  }

  const dispatchWork = selectedScenario?.is_gausium_map || selectedScenario?.is_gausium_executable_task
    ? undefined
    : selectedScenario || selectedMission
  const acceptedRobotTypes = robotTypesAcceptingWork(
    dispatchWork?.robot_type,
    selectedScenario && !selectedScenario.is_gausium_map && !selectedScenario.is_gausium_executable_task
      ? selectedScenario.maintenance
      : undefined
  )
  const workDispatchBlock = dispatchBlockReason({
    entityType: dispatchWork?.robot_type,
    robotType: selectedRobot?.robot_type,
    maintenance:
      selectedScenario && !selectedScenario.is_gausium_map && !selectedScenario.is_gausium_executable_task
        ? selectedScenario.maintenance
        : undefined,
  })

  const isSaveButtonDisabled =
    tempTaskName === '' ||
    hasNameError ||
    (!selectedMission && !selectedScenario) ||
    !selectedRobot ||
    (!date && !areArraysEqual(selectedWeekdays, [false, false, false, false, false, false, false])) ||
    Boolean(workDispatchBlock) ||
    (selectedMission && selectedMission.status !== MISSION_CALCULATING_STATUSES.OK) ||
    shouldBlockSave()

  const isGausiumSaveDisabled =
    gausiumLoading ||
    !selectedRobot?.id ||
    !gausiumMapName.trim() ||
    !tempTaskName.trim() ||
    !gausiumCleaningMode.trim() ||
    shouldBlockSave()
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const [scheduleTimezone, setScheduleTimezone] = useState<string>(() => robotTimezone ?? browserTimezone)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const isSingleScope = taskToEdit && eventScope === 'current'
  const isLaunchMode = !!legacyLaunchItem
  const isGausiumLaunchMode = !!(legacyLaunchItem?.gausiumMapPk || legacyLaunchItem?.gausiumExecutableTask)
  const isGausiumSelectedScenario = Boolean(
    selectedScenario?.is_gausium_map || selectedScenario?.is_gausium_executable_task
  )
  const isGausiumTaskMode =
    isGausiumLaunchMode ||
    Boolean(selectedScenario?.is_gausium_executable_task) ||
    (selectedRobot?.vendor === VENDOR.GAUSIUM && isGausiumSelectedScenario)

  useEffect(() => {
    if (!isOpen || isGausiumTaskMode) return
    const taskSettings = (taskToEdit as OdysseyRead | undefined)?.settings
    const profile = selectedScenario?.resource_profile ?? selectedMission?.resource_profile
    setPrerequisites(
      mergeOdysseySettings(taskSettings ?? selectedScenario?.settings ?? null, profile)
    )
  }, [
    isOpen,
    isGausiumTaskMode,
    taskToEdit,
    selectedScenario?.id,
    selectedScenario?.settings,
    selectedScenario?.resource_profile,
    selectedMission?.id,
    selectedMission?.resource_profile,
  ])

  const selectedTaskVendor: VendorCode | undefined =
    selectedScenario?.is_gausium_map || selectedScenario?.is_gausium_executable_task
      ? VENDOR.GAUSIUM
      : selectedMission || selectedScenario
      ? VENDOR.AUTONOMICS
      : undefined
  const selectedRobotVendor: VendorCode | undefined = selectedRobot?.vendor
  const enforcedVendor: VendorCode | undefined =
    selectRobotIncludeVendor || selectedRobotVendor || selectedTaskVendor || vendorFilter
  const lockVendorBySelection = Boolean(selectRobotIncludeVendor || selectedRobotVendor || selectedTaskVendor)
  const selectedGausiumRobotId: number | undefined =
    (selectedScenario?.is_gausium_map || selectedScenario?.is_gausium_executable_task) &&
    typeof selectedScenario?.gausiumRobotId === 'number'
      ? selectedScenario.gausiumRobotId
      : undefined

  const robotPickerVendor: VendorCode | undefined = enforcedVendor

  /** GS: scheduling via this modal is not used — keep Schedule visible but inactive. */
  const isScheduleTabDisabled =
    isGausiumTaskMode ||
    selectRobotIncludeVendor === VENDOR.GAUSIUM ||
    vendorFilter === VENDOR.GAUSIUM ||
    selectedRobot?.vendor === VENDOR.GAUSIUM

  const showPrerequisitesTab =
    !isGausiumTaskMode && FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS
  const prerequisitesTabIndex = 2

  const missionButtonDisabled =
    isLaunchMode || isGausiumLaunchMode || (Boolean(selectedRobotVendor) && selectedRobotVendor !== enforcedVendor)
  const robotButtonDisabled = isGausiumLaunchMode

  /** Dialog context is the input contract; field policy is derived from it. */
  const dialogContext: DialogContext = {
    target,
    preselected: effectiveLocks,
    pickerScope: pickerScope ?? {},
  }
  const hasAnySelection = Boolean(selectedRobot || selectedMission || selectedScenario || tempTaskName)
  const fieldPolicy = derivePolicy({
    context: dialogContext,
    effectiveVendor: enforcedVendor,
    hasAnySelection,
  })

  const isReadonlyFromGausiumExecSelection = Boolean(selectedScenario?.is_gausium_executable_task)
  const isRobotButtonDisabled = robotButtonDisabled || isReadonlyFromGausiumExecSelection || !fieldPolicy.canChangeRobot
  const isMissionButtonDisabled =
    missionButtonDisabled || isReadonlyFromGausiumExecSelection || !fieldPolicy.canChangeCleaningItem
  const effectiveRobotIdsFilter = (() => {
    if (!selectedGausiumRobotId) return robotIdsFilter
    if (!robotIdsFilter?.length) return [selectedGausiumRobotId]
    return robotIdsFilter.filter(id => id === selectedGausiumRobotId)
  })()

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab ?? 0)
      setVendorFilter(selectRobotIncludeVendor ?? VENDOR.AUTONOMICS)
    }
  }, [isOpen, initialTab, selectRobotIncludeVendor])

  useEffect(() => {
    if (isScheduleTabDisabled && tab === 1) {
      setTab(0)
    }
    if (!showPrerequisitesTab && tab === prerequisitesTabIndex) {
      setTab(0)
    }
  }, [isScheduleTabDisabled, showPrerequisitesTab, prerequisitesTabIndex, tab])

  const fetchJobMission = async (id: number) => {
    try {
      const mission = await MainApi.fetchMission(id)
      setSelectedMission(mission.mission)
    } catch (error) {
      setSelectedMission(undefined)
    }
  }

  const fetchTaskScenario = async (id: number) => {
    try {
      const scenario = await MainApi.fetchScenario(id)
      setSelectedScenario(scenario)
    } catch (error) {
      setSelectedScenario(undefined)
    }
  }

  const fetchOdysseyMissions = async (id: number[]) => {
    try {
      setSelectedMission(undefined)
      if (id.length > 0) {
        const mission = await MainApi.fetchMission(id[0])
        setSelectedMission(mission.mission)
      }
      // setSelectedMissions(prev => [...prev, undefined])
    } catch (error) {
      setSelectedMission(undefined)
    }
  }

  const fetchJobRobot = async (id: number) => {
    try {
      const robot = await MainService.mainRobotRetrieve(id)
      setSelectedRobot(robot)
    } catch (error) {
      setSelectedRobot(undefined)
    }
  }

  // const onRemoveMissionClick = (index: number) => {
  //   const updatedMissions = selectedMissions.filter((_, i) => i !== index)
  //   setSelectedMissions(updatedMissions)
  // }

  const resolveScheduleDateForForm = (
    datetime: string | null | undefined,
    tz: string,
    anchorDate?: Date | null
  ): Date | null => {
    if (!datetime) return null
    const wallClock = parseWallClockFromDatetime(datetime)
    if (!wallClock) return null
    const daySource =
      anchorDate ??
      (() => {
        const iso = datetime.replace(' ', 'T')
        const dayMatch = iso.match(/^(\d{4}-\d{2}-\d{2})/)
        return dayMatch ? new Date(`${dayMatch[1]}T12:00:00`) : null
      })()
    if (!daySource) return null
    const y = daySource.getFullYear()
    const mo = String(daySource.getMonth() + 1).padStart(2, '0')
    const d = String(daySource.getDate()).padStart(2, '0')
    const h = String(wallClock.hours).padStart(2, '0')
    const m = String(wallClock.minutes).padStart(2, '0')
    try {
      return zonedTimeToUtc(new Date(`${y}-${mo}-${d}T${h}:${m}:00`), tz)
    } catch {
      return setWallClockOnDate(daySource, wallClock.hours, wallClock.minutes, -new Date().getTimezoneOffset())
    }
  }

  // Prepare data for the edit case
  useEffect(() => {
    if (taskToEdit || legacyLaunchItem?.task) {
      const task = (legacyLaunchItem?.task || taskToEdit) as OdysseyRead
      let savedTimezone = task?.scheduler_settings?.timezone
      if (!savedTimezone && task?.scheduler_settings?.datetime) {
        const iso = task.scheduler_settings.datetime.trim().replace(' ', 'T')
        const match = iso.match(/([+-]\d{2}:\d{2}|Z)$/)
        if (match) {
          savedTimezone = match[1] === 'Z' ? 'UTC' : match[1]
        }
      }
      const taskTz = savedTimezone ?? robotTimezone ?? browserTimezone
      setScheduleTimezone(taskTz)

      setNameHasError(false)
      setTempTaskName(task.name || '')
      if ('scenario' in task && task.scenario) {
        if (typeof task.scenario === 'number') {
          fetchTaskScenario(task.scenario)
        } else {
          setSelectedScenario(task.scenario)
        }
        setSelectedMission(undefined)
      } else if ('missions' in task) {
        fetchOdysseyMissions(task.missions)
      }

      fetchJobRobot(task.robot_id)
      let initialDate: Date | null = null
      if (!isLaunchMode) {
        const isRecurringTask =
          task?.scheduler_settings?.weekdays && Object.values(task.scheduler_settings.weekdays).some(v => v === 'true')
        if (isRecurringTask) {
          // Single-occurrence edit must keep the clicked calendar day (dateToEdit).
          const anchor = dateToEdit ?? new Date()
          initialDate = resolveScheduleDateForForm(task?.scheduler_settings?.datetime, taskTz, anchor)
        } else if (dateToEdit) {
          initialDate = resolveScheduleDateForForm(task?.scheduler_settings?.datetime, taskTz, dateToEdit) ?? dateToEdit
        } else if (task?.scheduler_settings?.datetime) {
          initialDate = resolveScheduleDateForForm(task.scheduler_settings.datetime, taskTz)
        }
      }
      setDate(initialDate)
      setEndDate(null)
      setSelectedWeekdays(
        !isLaunchMode && task?.scheduler_settings?.weekdays
          ? [
              task.scheduler_settings.weekdays.SU === 'true',
              task.scheduler_settings.weekdays.MO === 'true',
              task.scheduler_settings.weekdays.TU === 'true',
              task.scheduler_settings.weekdays.WE === 'true',
              task.scheduler_settings.weekdays.TH === 'true',
              task.scheduler_settings.weekdays.FR === 'true',
              task.scheduler_settings.weekdays.SA === 'true',
            ]
          : [false, false, false, false, false, false, false]
      )
    } else if (isOpen && !legacyLaunchItem?.gausiumMapPk && !legacyLaunchItem?.gausiumExecutableTask) {
      // a general case - creating new task or launch
      setSelectedRobot(undefined)
      setSelectedMission(undefined)
      if (legacyLaunchItem?.scenario) {
        setSelectedScenario(legacyLaunchItem.scenario)
        setTempTaskName(legacyLaunchItem.scenario.name || '')
      } else if (legacyLaunchItem?.mission) {
        setSelectedMission(legacyLaunchItem.mission)
        setSelectedScenario(undefined)
        setTempTaskName(legacyLaunchItem.mission.name || '')
      } else {
        setSelectedScenario(undefined)
        setTempTaskName(taskName)
      }
      // New task: empty schedule → Save confirms "Execute immediately?" and
      // sends datetime null (robot task_time zeros). dateToEdit is for edit
      // of a calendar occurrence, not this create path.
      setDate(null)
      setEndDate(null)
      setSelectedWeekdays([false, false, false, false, false, false, false])
      setScheduleTimezone(robotTimezone ?? browserTimezone)
      setNameHasError(false)
    }
  }, [isOpen, taskToEdit, legacyLaunchItem, dateToEdit, taskName]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync timezone when robot is selected or changed
  useEffect(() => {
    if (robotTimezone) {
      setScheduleTimezone(robotTimezone)
    }
  }, [robotTimezone])

  const onTaskNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setTempTaskName(value)
    setNameHasError(value === '')
  }

  useEffect(() => {
    if (!isOpen || !legacyLaunchItem?.gausiumExecutableTask) return
    let cancelled = false
    const t = legacyLaunchItem.gausiumExecutableTask
    const run = async () => {
      setGausiumLoading(true)
      setGausiumError(null)
      try {
        setGausiumMapName((t.map_name || '').trim())
        setTempTaskName((t.task_name || '').trim())
        setSelectedScenario({
          id: t.id,
          name: t.task_name || '',
          is_gausium_executable_task: true,
          is_gausium_map: false,
          gausiumMapName: t.map_name,
          gausiumRobotId: t.robot_id,
          robot_type: '',
        })
        setSelectedMission(undefined)
        setNameHasError(false)
        const robot = await MainService.mainRobotRetrieve(t.robot_id)
        if (!cancelled) setSelectedRobot(robot)
      } catch (error) {
        if (!cancelled) setGausiumError(error instanceof Error ? error.message : 'Failed to load task')
      } finally {
        if (!cancelled) setGausiumLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [isOpen, legacyLaunchItem?.gausiumExecutableTask])

  useEffect(() => {
    if (!isOpen || !legacyLaunchItem?.gausiumMapPk || legacyLaunchItem?.gausiumExecutableTask) return
    let cancelled = false
    const run = async () => {
      setGausiumLoading(true)
      setGausiumError(null)
      try {
        const res = await MainApi.fetchGausiumMaps(
          undefined,
          undefined,
          undefined,
          undefined,
          legacyLaunchItem.gausiumMapPk
        )
        const row = res.results?.[0]
        if (!row?.robot_id) {
          if (!cancelled) setGausiumError('Map not found')
          return
        }
        if (cancelled) return
        const mapName = row.map_name || ''
        const mapPk = legacyLaunchItem.gausiumMapPk
        setGausiumMapName(mapName)
        setSelectedScenario({
          id: mapPk,
          name: mapName,
          is_gausium_map: true,
          robot_type: row.robot_type || '',
        })
        setSelectedMission(undefined)
        setNameHasError(false)
        const robotId = row.robot_id
        const robot = await MainService.mainRobotRetrieve(robotId)
        if (!cancelled) setSelectedRobot(robot)
        const exec = await RobotApi.fetchGausiumExecutableTasksRecent(robotId)
        if (cancelled) return
        const vid = (row.vendor_map_id || '').trim()
        const mn = (row.map_name || '').trim()
        const forMap = exec.filter(
          t => !vid || (t.vendor_map_id || '').trim() === vid || (t.map_name || '').trim() === mn
        )
        const rows: GausiumExecutableTaskRecent[] = forMap.length ? forMap : exec
        const first = rows[0]?.task_name || ''
        setTempTaskName(first)
      } catch (error) {
        if (!cancelled) setGausiumError(error instanceof Error ? error.message : 'Failed to load map')
      } finally {
        if (!cancelled) setGausiumLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [isOpen, legacyLaunchItem?.gausiumMapPk, legacyLaunchItem?.gausiumExecutableTask])

  /** Optional: pre-select robot when caller passes robotId (same layout as Autonomics). */
  useEffect(() => {
    if (!isOpen || !legacyLaunchItem?.robotId || isGausiumLaunchMode) return
    let cancelled = false
    MainService.mainRobotRetrieve(legacyLaunchItem.robotId)
      .then(r => {
        if (!cancelled) setSelectedRobot(r)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [isOpen, legacyLaunchItem?.robotId, isGausiumLaunchMode])

  const handleSchedulerChange = async (
    newDate: Date | null,
    newEndDate: Date | null,
    newSelectedWeekdays: boolean[]
  ) => {
    setDate(newDate)
    setEndDate(newEndDate)
    setSelectedWeekdays(newSelectedWeekdays)
  }

  function areArraysEqual(arr1: boolean[], arr2: boolean[]): boolean {
    if (arr1.length !== arr2.length) return false
    return arr1.every((value, index) => value === arr2[index])
  }

  const formatScheduledDatetime = (scheduledDate: Date): string => {
    try {
      return formatInTimeZone(scheduledDate, scheduleTimezone, "yyyy-MM-dd'T'HH:mm:ss")
    } catch {
      return scheduledDate.toISOString().replace(/Z$/, '')
    }
  }

  const isOdyssey = (): boolean => {
    return taskToEdit ? 'missions' in taskToEdit : !!selectedScenario
  }

  const createTask = async (
    missionIds: number[],
    robotId: number,
    schedulerSettings: SchedulerSettingsType,
    name: string,
    scenarioId?: number | null
  ) => {
    await RobotApi.createOdyssey(
      missionIds.slice(0, 1),
      robotId,
      schedulerSettings,
      name,
      scenarioId,
      prerequisites
    )
  }

  const updateTask = async (
    taskId: number,
    missionIds: number[],
    robotId: number,
    schedulerSettings: SchedulerSettingsType,
    name: string,
    scenarioId?: number | null
  ) => {
    await RobotApi.updateTheEntireOdyssey(
      taskId,
      missionIds.slice(0, 1),
      robotId,
      schedulerSettings,
      name,
      scenarioId,
      TASK_STATUSES.NEW,
      prerequisites
    )
  }

  const updateSchedule = async (taskId: number, schedulerSettings: SchedulerSettingsType) => {
    await MainApi.updateOdysseySchedule(taskId, schedulerSettings)
  }

  const deleteTask = async (taskId: number) => {
    await RobotApi.updateOdysseyType(taskId, TaskTypeEnum.DELETE_SCENARIO)
  }

  // The main logic to save or edit a task
  const executeSave = async () => {
    if (isGausiumTaskMode) {
      const effectiveMapName = (
        gausiumMapName ||
        selectedScenario?.gausiumMapName ||
        selectedScenario?.name ||
        ''
      ).trim()
      if (!selectedRobot?.id || !effectiveMapName || !tempTaskName.trim() || !gausiumCleaningMode.trim()) {
        return
      }
      await MainApi.postGausiumRemoteCommand(selectedRobot.id, {
        kind: 'start_task',
        map_name: effectiveMapName,
        task_name: tempTaskName.trim(),
        cleaning_mode: gausiumCleaningMode.trim(),
        loop: false,
        loop_count: 1,
        start_delay: GAUSIUM_REMOTE_COMMAND_START_DELAY_MS,
      })
      return
    }

    // sanity check
    if ((!selectedMission && !selectedScenario) || !selectedRobot) {
      return
    }

    // extract mission IDs
    let missionIds: number[] = []
    if (!selectedScenario && selectedMission) {
      missionIds = [selectedMission.id]
    }

    // prepare scheduler settings
    let weekdays = null
    if (!areArraysEqual(selectedWeekdays, [false, false, false, false, false, false, false])) {
      weekdays = selectedWeekdays
        ? {
            SU: selectedWeekdays[0].toString(),
            MO: selectedWeekdays[1].toString(),
            TU: selectedWeekdays[2].toString(),
            WE: selectedWeekdays[3].toString(),
            TH: selectedWeekdays[4].toString(),
            FR: selectedWeekdays[5].toString(),
            SA: selectedWeekdays[6].toString(),
          }
        : null
    }

    let targetDatetime = null
    if (date) {
      targetDatetime = formatScheduledDatetime(date)
    }

    const currentSettings = taskToEdit?.scheduler_settings
    let schedulerSettings: SchedulerSettingsType = {
      datetime: targetDatetime,
      timezone: scheduleTimezone,
      weekdays,
      except_days: currentSettings ? currentSettings.except_days : [],
      except_days_descriptions: currentSettings?.except_days_descriptions || {},
      except_days_users: currentSettings?.except_days_users || {},
    }

    try {
      // A general case: just create a new task
      if (!taskToEdit) {
        await createTask(missionIds, selectedRobot.id, schedulerSettings, tempTaskName, selectedScenario?.id)
        return
      }

      // We have only one specific case: editing of the single event of a recurring task
      if (isSingleScope) {
        // Recurring series, this occurrence only: skip the original day on the robot
        // (except_days) and mark it Rescheduled for FMS. A one-shot Odyssey runs the new time.
        const parentSettings = taskToEdit.scheduler_settings
        if (parentSettings && hasRecurringWeekdays(parentSettings.weekdays) && date && dateToEdit) {
          const originalDate = toISOStringYYYYMMDD(dateToEdit)
          const updated_scheduler_settings = withSingleOccurrenceReschedule(parentSettings, originalDate)

          schedulerSettings = {
            datetime: targetDatetime,
            timezone: scheduleTimezone,
            weekdays: null,
            except_days: [],
          }
          await updateSchedule(taskToEdit.id, updated_scheduler_settings)
          await createTask(missionIds, selectedRobot.id, schedulerSettings, tempTaskName, selectedScenario?.id)
          return
        }
      }
      // The common case for editing the entire task
      if (selectedRobot?.id !== taskToEdit.robot_id) {
        await deleteTask(taskToEdit.id)
        await createTask(missionIds, selectedRobot.id, schedulerSettings, tempTaskName, selectedScenario?.id)
      } else {
        await updateTask(
          taskToEdit.id,
          missionIds,
          taskToEdit.robot_id,
          schedulerSettings,
          tempTaskName,
          selectedScenario?.id
        )
      }
    } catch (error) {
      console.log('Error saving task: ', error)
    }
  }

  const proceedSave = async () => {
    if (!date) {
      setIsConfirmationOpen(true)
      return
    }
    try {
      await executeSave()
      onSubmit(true)
      resetData()
    } catch (error) {
      if (isGausiumTaskMode) {
        setGausiumError(error instanceof Error ? error.message : 'Command failed')
      }
    }
  }

  const onSaveClicked = async () => {
    const resources = getProblematicRobotResources(selectedRobot)
    const shouldShowResourceWarning =
      selectedRobot?.vendor === VENDOR.AUTONOMICS && isInstantTask() && resources.length > 0
    if (shouldShowResourceWarning) {
      setProblematicResources(resources)
      setResourceWarningOpen(true)
      return
    }
    await proceedSave()
  }

  const handleConfirmSave = async () => {
    setIsConfirmationOpen(false)
    try {
      await executeSave()
      onSubmit(true)
      resetData()
    } catch (error) {
      if (isGausiumTaskMode) {
        setGausiumError(error instanceof Error ? error.message : 'Command failed')
      }
    }
  }

  const resetData = () => {
    setSelectedRobot(undefined)
    setSelectedMission(undefined)
    setSelectedScenario(undefined)
    setDate(null)
    setSelectedWeekdays([false, false, false, false, false, false, false])
    setEndDate(null)
    setScheduleTimezone(robotTimezone ?? browserTimezone)
    setTempTaskName(taskName)
    setNameHasError(false)
    setGausiumMapName('')
    setGausiumCleaningMode('__middle_cleaning')
    setGausiumLoading(false)
    setGausiumError(null)
    setResourceWarningOpen(false)
    setProblematicResources([])
    setVendorFilter(selectRobotIncludeVendor ?? VENDOR.AUTONOMICS)
    setPrerequisites(mergeOdysseySettings())
  }

  /** User-driven Reset handler honoring derived resetMode from fieldPolicy. */
  const onResetClicked = () => {
    if (fieldPolicy.resetMode === 'full') {
      resetData()
    }
  }

  const onCancelClicked = () => {
    onClose(true)
    resetData()
  }
  const onSelectRobotClick = () => {
    setShowRobotModal(true)
  }

  const onSelectMissionClick = () => {
    setShowMissionModal(true)
  }

  const getNumericResourceValue = (...candidates: unknown[]): number | null => {
    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate
      }
    }
    return null
  }

  const getRobotBatteryLevel = (robot: RobotRead | undefined): number | null => {
    const raw = robot as any
    return getNumericResourceValue(
      robot?.battery_power,
      raw?.telemetry_last?.robot_battery?.power,
      raw?.telemetry_last?.battery_power,
      raw?.telemetry_last_point?.battery_power
    )
  }

  const getRobotCleanWaterLevel = (robot: RobotRead | undefined): number | null => {
    const raw = robot as any
    return getNumericResourceValue(
      robot?.water_level,
      raw?.telemetry_last?.water_level,
      raw?.telemetry_last?.clean_water_level,
      raw?.telemetry_last_point?.water_level
    )
  }

  const getAutonomicsResourceThresholds = (): { battery: number; cleanWater: number } => {
    if (!FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS) {
      return {
        battery: DEFAULT_AUTONOMICS_RESOURCE_THRESHOLD_PCT,
        cleanWater: DEFAULT_AUTONOMICS_RESOURCE_THRESHOLD_PCT,
      }
    }
    return {
      battery: Number.isFinite(prerequisites.battery)
        ? prerequisites.battery
        : DEFAULT_AUTONOMICS_RESOURCE_THRESHOLD_PCT,
      cleanWater: Number.isFinite(prerequisites.clean_water_level)
        ? prerequisites.clean_water_level
        : DEFAULT_AUTONOMICS_RESOURCE_THRESHOLD_PCT,
    }
  }

  const getProblematicRobotResources = (robot: RobotRead | undefined): string[] => {
    const batteryLevel = getRobotBatteryLevel(robot)
    const cleanWaterLevel = getRobotCleanWaterLevel(robot)
    const { battery: batThreshold, cleanWater: waterThreshold } = getAutonomicsResourceThresholds()
    const resources: string[] = []
    if (batteryLevel !== null && batteryLevel < batThreshold) {
      resources.push(`Battery: now ${Math.round(batteryLevel)}%, need ${Math.round(batThreshold)}%`)
    }
    if (cleanWaterLevel !== null && cleanWaterLevel < waterThreshold) {
      resources.push(`Clean water: now ${Math.round(cleanWaterLevel)}%, need ${Math.round(waterThreshold)}%`)
    }
    return resources
  }

  const applySelectedRobot = (robot: RobotRead | undefined) => {
    setSelectedRobot(robot)
    if (robot?.vendor) {
      setVendorFilter(robot.vendor)
      if (
        robot.vendor === VENDOR.GAUSIUM &&
        selectedMission &&
        !selectedScenario?.is_gausium_map &&
        !selectedScenario?.is_gausium_executable_task
      ) {
        setSelectedMission(undefined)
      }
      if (
        robot.vendor === VENDOR.AUTONOMICS &&
        (selectedScenario?.is_gausium_map || selectedScenario?.is_gausium_executable_task)
      ) {
        setSelectedScenario(undefined)
        setGausiumMapName('')
      }
    }
  }

  const onMissionSelected = async (mission: IMissionInfo | any | undefined) => {
    setShowMissionModal(false)
    if (!mission) return
    if (mission.is_scenario) {
      setSelectedScenario(mission)
      setSelectedMission(undefined)
      if (mission.is_gausium_map) {
        setVendorFilter(VENDOR.GAUSIUM)
        setGausiumMapName((mission.gausiumMapName || mission.name || '').trim())
        if (typeof mission.gausiumRobotId === 'number') {
          try {
            const gsRobot = await MainService.mainRobotRetrieve(mission.gausiumRobotId)
            setSelectedRobot(gsRobot)
          } catch (error) {
            // keep currently selected robot if GS robot fetch fails
          }
        }
      } else if (mission.is_gausium_executable_task) {
        setVendorFilter(VENDOR.GAUSIUM)
        setGausiumMapName((mission.gausiumMapName || '').trim())
        if (typeof mission.gausiumRobotId === 'number') {
          try {
            const gsRobot = await MainService.mainRobotRetrieve(mission.gausiumRobotId)
            setSelectedRobot(gsRobot)
          } catch (error) {
            // keep currently selected robot if GS robot fetch fails
          }
        }
      }
    } else {
      setVendorFilter(VENDOR.AUTONOMICS)
      setSelectedScenario(undefined)
      setSelectedMission(mission)
      setGausiumMapName('')
      if (selectedRobot?.vendor === VENDOR.GAUSIUM) {
        setSelectedRobot(undefined)
      }
    }
    setTempTaskName(mission.name)
  }

  const onRobotSelected = async (robot: RobotRead | undefined) => {
    setShowRobotModal(false)
    applySelectedRobot(robot)
  }

  const onConfirmLowResourceSave = async () => {
    setResourceWarningOpen(false)
    await proceedSave()
  }

  const onCancelLowResourceSave = () => {
    setSelectedRobot(undefined)
    setResourceWarningOpen(false)
    setProblematicResources([])
  }

  useEffect(() => {
    if (!isOpen) {
      setResourceWarningOpen(false)
      setProblematicResources([])
    }
  }, [isOpen])

  const getDiagnosticStatusClass = createDiagnosticStatusClassGetter(diagnosticStyles)

  const hasSystemDiagnostics = () => {
    return selectedRobot?.system_diagnostics !== null && selectedRobot?.system_diagnostics !== undefined
  }

  const shouldShowDiagnostics = () => {
    return (
      hasSystemDiagnostics() &&
      selectedRobot?.system_diagnostics &&
      shouldShowDiagnosticsUtil(selectedRobot.system_diagnostics.level)
    )
  }

  useEffect(() => {
    if (!isOpen || !selectedRobot?.id) {
      return
    }

    const robotId = selectedRobot.id

    const updateRobotData = async () => {
      try {
        const robot = await MainService.mainRobotRetrieve(robotId)
        setSelectedRobot(robot)
      } catch (error) {
        console.error('[TaskSettingsModal] Failed to update robot data:', error)
      }
    }

    const initialTimeout = setTimeout(() => {
      updateRobotData()
    }, 1000)

    const interval = setInterval(() => {
      updateRobotData()
    }, ROBOT_DIAGNOSTICS_POLLING_INTERVAL)

    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [isOpen, selectedRobot?.id])

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCancelClicked}
      className={style.root}
      style={{
        overlay: {
          zIndex: 100,
          backgroundColor: 'rgba(27, 43, 72, 0.6)',
        },
      }}
      ariaHideApp={false}
    >
      <div className={style.modal}>
        <div className={style.head}>
          <Title text={taskToEdit ? 'Edit the scheduled task' : 'Add a new task'} className={style.title} />
        </div>
        {/*
          Padding wrapper (not margin on Tabs): AdaptiveTabs measures root.clientWidth.
          Horizontal margins do not shrink clientWidth, so fullWidth stayed on and labels
          looked clipped with no scroll / jitter on resize.
        */}
        <div className={style.tabsBar}>
          <AdaptiveTabs
            value={tab}
            onChange={(e, newValue: number) => {
              setTab(newValue)
            }}
            indicatorColor="primary"
            sx={{ height: 45, minHeight: 45 }}
          >
            <StyledTab
              label="Main"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
            <StyledTab
              label={'Schedule'}
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
              disabled={isScheduleTabDisabled}
            />
            {showPrerequisitesTab && (
              <StyledTab
                label={t('prerequisites.title')}
                sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
                disableRipple
              />
            )}
          </AdaptiveTabs>
        </div>
        <TabPanel value={tab} index={0} disablePadding={true}>
          <div className={style.content}>
            <TextFieldDescribed
              className={style.textfieldFullWidth}
              description={'Task name'}
              error={'Invalid task name'}
              placeholder={'Task name'}
              hasError={hasNameError}
              value={tempTaskName}
              disabled={isGausiumTaskMode || !fieldPolicy.canEditTaskName}
              onChange={onTaskNameChanged}
            />
            <div className={style.selectBlock}>
              <TextFieldDescribed
                className={style.textfieldFullWidth}
                description={'Robot'}
                placeholder={'Not selected'}
                value={
                  selectedRobot && selectedRobot.serial_number
                    ? `SN: ${selectedRobot.serial_number}, Name: ${selectedRobot.name}`
                    : 'Not selected'
                }
                disabled
                onChange={() => {}}
              />
              <Button.Outlined
                className={style.btnSelect}
                onClick={onSelectRobotClick}
                disabled={isRobotButtonDisabled}
              >
                Select a Robot
              </Button.Outlined>
              <SelectRobotModal
                isOpen={showRobotModal}
                robotType={
                  acceptedRobotTypes ? undefined : selectedMission || selectedScenario ? robot_type : undefined
                }
                acceptedRobotTypes={acceptedRobotTypes}
                includeVendor={robotPickerVendor}
                selectedVendor={enforcedVendor}
                onVendorChange={v => setVendorFilter(v)}
                lockVendor={lockVendorBySelection}
                onClose={() => setShowRobotModal(false)}
                onConfirmClick={onRobotSelected}
                robotIdsFilter={effectiveRobotIdsFilter}
              />
            </div>
            {shouldShowDiagnostics() && selectedRobot?.system_diagnostics && (
              <div className={style.diagnosticsBlock}>
                <div className={style.diagnosticsLabel}>System Diagnostics</div>
                <div className={style.diagnosticsContent}>
                  <div
                    className={clsx(
                      style.diagnosticsChip,
                      getDiagnosticStatusClass(selectedRobot.system_diagnostics.level)
                    )}
                  >
                    {selectedRobot.system_diagnostics.name?.trim() ||
                      getDiagnosticStatusText(selectedRobot.system_diagnostics.level)}
                  </div>
                  <div className={style.diagnosticsMessage}>
                    {selectedRobot.system_diagnostics.message?.trim() || '-'}
                  </div>
                </div>
              </div>
            )}
            {(() => {
              const mission = selectedScenario || selectedMission
              const is_scenario =
                selectedScenario ||
                (taskToEdit as OdysseyRead)?.scenario ||
                mission?.is_scenario ||
                selectedScenario?.is_gausium_map ||
                selectedScenario?.is_gausium_executable_task

              return (
                <div key="single-mission">
                  <div className={style.missionRowContainer}>
                    <div className={style.selectBlock}>
                      <TextFieldDescribed
                        className={style.textfieldFullWidth}
                        description={
                          selectedScenario?.is_gausium_executable_task
                            ? 'Gausium task'
                            : selectedScenario?.is_gausium_map
                            ? 'Mission/Scenario'
                            : is_scenario
                            ? `Scenario`
                            : mission
                            ? `Mission`
                            : `Mission/Scenario`
                        }
                        placeholder={'Not selected'}
                        value={
                          mission && mission.id
                            ? selectedScenario?.is_gausium_executable_task
                              ? `${mission.name} (map: ${
                                  (mission.gausiumMapName || mission.map_name || '').trim() || '—'
                                })`
                              : `ID: ${mission.id}, Name: ${mission.name}`
                            : 'Not selected'
                        }
                        disabled
                        onChange={() => {}}
                      />
                      <Button.Outlined
                        className={style.btnSelect}
                        onClick={onSelectMissionClick}
                        disabled={isMissionButtonDisabled}
                      >
                        Mission/Scenario
                      </Button.Outlined>
                    </div>
                  </div>
                </div>
              )
            })()}
            <SelectMissionModal
              isOpen={showMissionModal}
              robotType={robot_type}
              dispatchToRobot={Boolean(selectedRobot) && !isGausiumTaskMode}
              showScenariosTab={true}
              hideInconsistentScenarios={true}
              selectedVendor={enforcedVendor}
              onVendorChange={v => setVendorFilter(v)}
              lockVendor={lockVendorBySelection}
              selectedRobotId={selectedRobot?.id}
              selectedRobotSerial={selectedRobot?.serial_number || undefined}
              onClose={() => setShowMissionModal(false)}
              onConfirmClick={onMissionSelected}
            />
            {gausiumError && <div className={style.error}>{gausiumError}</div>}
            {workDispatchBlock && <div className={style.error}>{workDispatchBlock}</div>}
          </div>
        </TabPanel>
        <TabPanel value={tab} index={1} disablePadding={true}>
          <div className={style.content}>
            <SchedulerSettings
              initialDate={date}
              initialEndDate={endDate}
              initialWeekdays={selectedWeekdays}
              timezone={scheduleTimezone}
              showWeekdaysSelection={!isSingleScope}
              onSchedulerChange={handleSchedulerChange}
              onTimezoneChange={setScheduleTimezone}
              robotTimezone={robotTimezone ?? null}
            />
          </div>
        </TabPanel>
        {showPrerequisitesTab && (
          <TabPanel value={tab} index={prerequisitesTabIndex} disablePadding={true}>
            <div className={style.content}>
              <PrerequisitesSettingsForm
                value={prerequisites}
                profile={selectedScenario?.resource_profile ?? selectedMission?.resource_profile}
                hideDirtyWater={isSweeperRobotType(robot_type || selectedRobot?.robot_type)}
                onChange={setPrerequisites}
              />
            </div>
          </TabPanel>
        )}
        <div className={style.footer}>
          {shouldBlockSave() && (
            <div className={style.warningBlock}>
              Cannot send task to robot due to diagnostics. You can only schedule the task.
            </div>
          )}
          <div className={style.footerButtons}>
            {fieldPolicy.showReset && (
              <Button.Outlined className={style.btn} style={{ marginRight: 'auto' }} onClick={onResetClicked}>
                Reset
              </Button.Outlined>
            )}
            <Button.Outlined className={style.btn} onClick={onCancelClicked}>
              {t('mission_settings.label_cancel')}
            </Button.Outlined>
            <Button.Standart
              disabled={isGausiumTaskMode ? isGausiumSaveDisabled : isSaveButtonDisabled}
              className={style.btn}
              onClick={onSaveClicked}
            >
              {t('mission_settings.label_save')}
            </Button.Standart>
          </div>
        </div>
        <ConfirmDialog
          isOpen={isConfirmationOpen}
          onClose={() => setIsConfirmationOpen(false)}
          onConfirmClick={handleConfirmSave}
          title="The task doesn't have a schedule. Execute immediately?"
        />
        <ConfirmDialog
          isOpen={resourceWarningOpen}
          onClose={onCancelLowResourceSave}
          onConfirmClick={onConfirmLowResourceSave}
          title="Low robot resources"
          cancelText="Cancel"
          confirmText="Confirm"
          overlayZIndex={110}
        >
          <div className={style.resourceWarningDialog}>
            <div className={style.resourceWarningText}>
              The selected robot has low resources. It is possible that resources will be insufficient for this task.
            </div>
            <ul className={style.resourceWarningList}>
              {problematicResources.map(resource => (
                <li key={resource}>{resource}</li>
              ))}
            </ul>
          </div>
        </ConfirmDialog>
      </div>
    </Modal>
  )
}

export default TaskSettingsModal
